import { useEffect, useState } from "react";
import { X, Loader2, Mail, KeyRound, CheckCircle2, Gift, CalendarPlus, Building2, Activity, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const dt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return "—"; } };

function Row({ label, children }) {
  return <div className="flex justify-between gap-4 py-2 border-b border-white/5 text-sm">
    <span className="text-[#71717A]">{label}</span><span className="text-[#D4D4D8] text-right">{children}</span></div>;
}

export default function AdminUserDrawer({ userId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState({ name: "", company: "", phone: "" });
  const [credit, setCredit] = useState({ amount: "", note: "" });
  const [trialDays, setTrialDays] = useState("14");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/admin/users/${userId}`).then((r) => {
    setD(r.data);
    setForm({ name: r.data.user.name || "", company: r.data.user.company || "", phone: r.data.user.phone || "" });
  }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  const run = async (fn, msg, key) => {
    setBusy(key);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!userId) return null;
  const TABS = [["profile", "Profile"], ["subscription", "Subscription"], ["business", "Business"], ["activity", "Activity"]];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-user-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-[#0F0F11] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-[#0F0F11] border-b border-white/8 px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 className="text-white font-medium truncate">{d?.user.name || "User"}</h3>
            <p className="text-xs text-[#71717A] truncate">{d?.user.email}</p>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-[#71717A] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!d ? <div className="flex justify-center py-24 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="p-6">
          <div className="flex gap-1.5 mb-5 overflow-x-auto">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`drawer-tab-${id}`}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tab === id ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "text-[#A1A1AA] border border-white/8"}`}>{label}</button>
            ))}
          </div>

          {tab === "profile" && (
            <div className="space-y-4" data-testid="drawer-profile">
              {["name", "company", "phone"].map((f) => (
                <div key={f}>
                  <label className="text-xs text-[#71717A] capitalize">{f}</label>
                  <Input value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    data-testid={`drawer-${f}`} className="bg-[#121214] border-white/10 text-white mt-1" />
                </div>
              ))}
              <Button onClick={() => run(() => api.put(`/admin/users/${userId}`, form), "Profile updated", "save")}
                disabled={busy === "save"} data-testid="drawer-save-profile" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
                {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}</Button>

              <div className="pt-2 space-y-2">
                <Row label="Email verified">{d.user.is_verified ? <span className="text-emerald-400">Yes</span> : <span className="text-amber-400">No</span>}</Row>
                <Row label="2FA">{d.user.two_fa_enabled ? "Enabled" : "Off"}</Row>
                <Row label="Joined">{dt(d.user.created_at)}</Row>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {!d.user.is_verified && <Button variant="outline" onClick={() => run(() => api.post(`/admin/users/${userId}/verify-email`), "Email verified", "verify")}
                  disabled={busy === "verify"} data-testid="drawer-verify-email" className="border-white/15 bg-transparent text-[#D4D4D8] rounded-full text-xs"><CheckCircle2 className="w-4 h-4 mr-1" /> Verify email</Button>}
                <Button variant="outline" onClick={() => run(async () => { const r = await api.post(`/admin/users/${userId}/reset-password`); if (r.data.reset_url) { await navigator.clipboard?.writeText(r.data.reset_url).catch(() => {}); } }, "Reset link generated (copied)", "reset")}
                  disabled={busy === "reset"} data-testid="drawer-reset-password" className="border-white/15 bg-transparent text-[#D4D4D8] rounded-full text-xs"><KeyRound className="w-4 h-4 mr-1" /> Reset password</Button>
              </div>
            </div>
          )}

          {tab === "subscription" && (
            <div className="space-y-4" data-testid="drawer-subscription">
              <Row label="Plan"><span className="capitalize text-[#D4AF37]">{d.user.subscription?.tier || "free"}</span></Row>
              <Row label="Status"><span className="capitalize">{d.user.subscription?.status || "inactive"}</span></Row>
              <Row label="Trial ends">{d.user.subscription?.trial_ends_at ? dt(d.user.subscription.trial_ends_at) : "—"}</Row>
              <Row label="Credit balance"><span className="text-emerald-400">${Number(d.user.credit_balance || 0).toFixed(2)}</span></Row>

              <div className="rounded-xl border border-white/8 p-4 space-y-2">
                <p className="text-xs uppercase tracking-wider text-[#71717A]">Extend free trial</p>
                <div className="flex gap-2">
                  <Input type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} data-testid="drawer-trial-days" className="bg-[#121214] border-white/10 text-white w-24" />
                  <Button onClick={() => run(() => api.post(`/admin/users/${userId}/extend-trial`, { days: parseInt(trialDays, 10) }), "Trial extended", "trial")}
                    disabled={busy === "trial"} data-testid="drawer-extend-trial" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full text-xs"><CalendarPlus className="w-4 h-4 mr-1" /> Extend</Button>
                </div>
              </div>

              <div className="rounded-xl border border-white/8 p-4 space-y-2">
                <p className="text-xs uppercase tracking-wider text-[#71717A]">Issue account credit</p>
                <div className="flex gap-2">
                  <Input type="number" placeholder="$" value={credit.amount} onChange={(e) => setCredit({ ...credit, amount: e.target.value })} data-testid="drawer-credit-amount" className="bg-[#121214] border-white/10 text-white w-24" />
                  <Input placeholder="Note" value={credit.note} onChange={(e) => setCredit({ ...credit, note: e.target.value })} data-testid="drawer-credit-note" className="bg-[#121214] border-white/10 text-white flex-1" />
                </div>
                <Button onClick={() => run(() => api.post(`/admin/users/${userId}/credit`, { amount: parseFloat(credit.amount), note: credit.note }), "Credit applied", "credit")}
                  disabled={busy === "credit"} data-testid="drawer-issue-credit" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full text-xs w-full"><Gift className="w-4 h-4 mr-1" /> Apply credit</Button>
              </div>
              {d.user.account_credits?.length > 0 && (
                <div className="text-xs text-[#71717A] space-y-1">
                  {d.user.account_credits.slice(-5).reverse().map((c, i) => <div key={i}>${c.amount.toFixed(2)} · {c.note || "—"} · {dt(c.at)}</div>)}
                </div>
              )}
            </div>
          )}

          {tab === "business" && (
            <div className="space-y-3" data-testid="drawer-business">
              <div className="grid grid-cols-2 gap-3">
                {[["Documents", d.stats.documents], ["Contracts", d.stats.contracts], ["Goals", d.stats.goals], ["AI chats", d.stats.ai_conversations]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-white/8 p-3"><div className="font-heading text-xl text-white">{v}</div><div className="text-xs text-[#71717A]">{l}</div></div>
                ))}
              </div>
              {d.business_profile ? (
                <div className="rounded-xl border border-white/8 p-4">
                  <div className="flex items-center gap-2 mb-2 text-[#D4AF37]"><Building2 className="w-4 h-4" /><span className="text-sm">Business profile</span></div>
                  <Row label="Business name">{d.business_profile.business_name || d.business_profile.name || "—"}</Row>
                  <Row label="State">{d.business_profile.state || "—"}</Row>
                  <Row label="Entity type">{d.business_profile.entity_type || "—"}</Row>
                  <Row label="Formation status">{d.business_profile.workflow?.status || "—"}</Row>
                </div>
              ) : <p className="text-sm text-[#71717A]">No business profile yet.</p>}
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-4" data-testid="drawer-activity">
              <div>
                <div className="flex items-center gap-2 mb-2 text-[#D4AF37]"><ShieldCheck className="w-4 h-4" /><span className="text-sm">Login & security history</span></div>
                <div className="space-y-1">
                  {d.login_history.length ? d.login_history.map((e, i) => (
                    <div key={i} className="text-xs flex justify-between border-b border-white/5 py-1.5">
                      <span className="text-[#D4D4D8]">{e.event}</span><span className="text-[#52525B]">{dt(e.created_at)}</span></div>
                  )) : <p className="text-xs text-[#71717A]">No login history.</p>}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2 text-[#D4AF37]"><Activity className="w-4 h-4" /><span className="text-sm">Recent activity</span></div>
                <div className="space-y-1">
                  {d.activity.length ? d.activity.map((a, i) => (
                    <div key={i} className="text-xs flex justify-between border-b border-white/5 py-1.5">
                      <span className="text-[#D4D4D8]">{a.title || a.kind}</span><span className="text-[#52525B]">{dt(a.created_at)}</span></div>
                  )) : <p className="text-xs text-[#71717A]">No activity recorded.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
